import React from 'react';
import { useIntl } from 'react-intl';
import translations from './translations';
import { defaultLang, breakoutRoomLink } from './constants';
import {
  format,
  isAfter,
  isBefore,
  compareDesc,
  intlFormat,
  subMinutes,
} from 'date-fns';
import ical from 'ical-generator';
import { breakoutRoomModel } from 'entities/breakoutRoomModel';

export const useDataHelper = () => {
  // Hooks
  const intl = useIntl();
  const defaultValue = {
    brandName: '',
    date: '',
    start: '',
    end: '',
  };

  /**
   * name: brandName,
   * start: slot.start,
   * end: slot.end,
   * location: hall - pavillion,
   */

  const generateIcalBlob = ({
    isChatType,
    breakoutRoomInviteInfo = defaultValue,
  }) => {
    if (breakoutRoomInviteInfo !== defaultValue) {
      const i18n = translations.detailsPage(intl);
      const summary = getSummaryMessage({
        isChatType,
        info: breakoutRoomInviteInfo,
      });
      const name = `${i18n.icalName} ${breakoutRoomInviteInfo.brandName}`;
      const origin = window.location.origin;
      const domain = window.location.hostname;
      const data = {
        start: breakoutRoomInviteInfo.start,
        end: breakoutRoomInviteInfo.end,
        summary: summary,
        location: '',
        url: `${origin}/`,
      };
      const cal = ical({ domain: domain, name: name });
      cal.createEvent(data);
      return cal.toBlob();
    }
    return undefined;
  };

  const generateAgendaBlob = ({ agendaData }) => {
    const i18n = translations.detailsPage(intl);
    const name = `${i18n.icalName} ${agendaData.brandName}`;
    const origin = window.location.origin;
    const domain = window.location.hostname;
    const data = {
      start: agendaData.start,
      end: agendaData.end,
      location: `${agendaData.stand} - ${agendaData.hall}`,
      url: `${origin}/`,
    };
    const cal = ical({ domain: domain, name: name });
    cal.createEvent(data);
    return cal.toBlob();
  };

  const getSummaryMessage = ({ isChatType, info = defaultValue }) => {
    if (info !== defaultValue) {
      const i18n = translations.detailsPage(intl);
      let summary = isChatType ? i18n.summaryChat : i18n.summary;
      summary = summary.replace('##BRAND##', info.brandName);
      if (isChatType) {
        summary = summary.replace('##START##', getDateTime(info.start));
        summary = summary.replace('##END##', getDateTime(info.end));
      } else {
        summary = summary.replace('##DAY##', getDate(info.date));
        summary = summary.replace('##START##', getHours(info.start));
        summary = summary.replace('##END##', getHours(info.end));
      }
      return summary;
    }
    return undefined;
  };

  const getTooltipText = (br, isChatType) => {
    if (br) {
      const i18n = translations.detailsPage(intl);
      let tooltip = isChatType ? i18n.enterChatTooltip : i18n.enterTooltip;
      if (isChatType) {
        tooltip = tooltip.replace('##START##', getDateTime(br.start));
        tooltip = tooltip.replace('##END##', getDateTime(br.end));
      } else {
        tooltip = tooltip.replace('##DATE##', getDate(br.date));
        tooltip = tooltip.replace(
          '##START##',
          getHours(subMinutes(new Date(br.start), 10))
        );
        tooltip = tooltip.replace('##END##', getHours(br.end));
      }
      return tooltip;
    }
    return '';
  };

  const getHours = (hhmm) => {
    try {
      if (hhmm) return format(new Date(hhmm), 'HH:mm');
      return undefined;
    } catch (error) {
      return hhmm;
    }
  };

  const getDate = (date) => {
    try {
      if (date) return format(new Date(date), 'dd/MM/yyyy');
      return undefined;
    } catch (error) {
      return date;
    }
  };

  const getDateTime = (date) => {
    try {
      if (date) return format(new Date(date), 'dd/MM/yyyy HH:mm');
      return undefined;
    } catch (error) {
      return date;
    }
  };

  const getIntlDate = (date) => {
    return intlFormat(
      new Date(date),
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      {
        locale: getLangCode(intl.locale),
      }
    );
  };

  const getFormattedDate = (br, showDate = true, direction = 'column') => {
    let formattedDate = '';
    const stime = getHours(br.start);
    const etime = getHours(br.end);

    const isChatType = () => {
      return br.type === breakoutRoomModel.enums.types.CHAT;
    };

    const getShowDate = (date, separator = '') => {
      return `${showDate ? `${date} ${separator} ` : ''}`;
    };

    if (isChatType()) {
      // prettier-ignore
      const sdate = getIntlDate(br.start);
      const edate = getIntlDate(br.end);
      if(sdate !== edate)
        // prettier-ignore
        formattedDate = (<><div>{`${getShowDate(sdate,'-')}${stime}`}</div><div>{`${getShowDate(edate,'-')}${etime}`}</div></>);
      else
        // prettier-ignore
        formattedDate = (
          <>
            <div>{`${getShowDate(sdate)}`}</div>
            <div>{`${stime} - ${etime}`}</div>
          </>
        );
    } else {
      formattedDate = (
        <>
          <div>{`${getShowDate(getIntlDate(br.date))}`}</div>
          <div>{`${stime} - ${etime}`}</div>
        </>
      );
    }
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: direction || 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: 5,
        }}
      >
        {formattedDate}
      </div>
    );
  };

  const isBetween = (
    target = new Date(),
    start = new Date(),
    end = new Date()
  ) => {
    let flag = isAfter(target, start) && isBefore(target, end);
    return flag;
  };

  const orderBreakoutRooms = (list) => {
    const ordered = list.sort((a, b) => {
      return compareDesc(new Date(a.start), new Date(b.start));
    });
    return ordered;
  };

  const getLangCode = (lang) => {
    return `${lang.toLowerCase()}-${lang.toUpperCase()}`;
  };

  const capitalizeFirst = (str) => {
    return str[0].toUpperCase() + str.substring(1);
  };

  const checkPassedDate = (date) => {
    if (date) {
      return isAfter(new Date(), new Date(date));
    }
    return false;
  };

  const checkEnterButtonIsDisabled = (invitation) => {
    const now = new Date();

    const start = subMinutes(new Date(invitation.start), 10);
    const end = new Date(invitation.end);
    const flag = !isBetween(now, start, end);
    return flag;
  };

  const joinBr = (brId, email) => {
    window.location = `${breakoutRoomLink}${brId}?lang=${
      intl?.locale || intl?.defaultLocale || defaultLang
    }&email=${email}`;
  };

  const joinChat = (brId, salesforce_id = null, justLink = false) => {
    const link = `${window.location.origin}/${
      intl?.locale || intl?.defaultLocale || defaultLang
    }/chat?${
      salesforce_id && !justLink ? `salesforce_id=${salesforce_id}&` : ''
    }session=${brId}`;
    if (justLink) return link;
    else window.location = link;
  };

  const backToBr = (salesforce_id = null) => {
    window.location = `${window.location.origin}/${
      intl?.locale || intl?.defaultLocale || defaultLang
    }/breakout${salesforce_id ? `?salesforce_id=${salesforce_id}` : ''}`;
  };

  const parseRichText = (str) => {
    if (str) {
      try {
        const jsonParsed = JSON.parse(str);
        return jsonParsed.blocks[0].text ? jsonParsed : null;
      } catch (e) {
        console.warn(e);
      }
    }
    return null;
  };

  // const setDate = (date, hhmm) => {
  //   const arr = hhmm.split(':');
  //   return set(date, {
  //     hours: Number(arr[0]),
  //     minutes: Number(arr[1]),
  //   }).toISOString();
  // };
  /**
   *
   * @param {*} obj
   * @param {*} param
   * @returns
   */
  const getLocalizedParameter = (obj, param, locale) => {
    const i18n = translations.ExhibitorsList(intl).detailPage;
    return (
      obj?.[`${param}_${locale}`] ??
      obj?.[`${param}_${defaultLang}`] ??
      i18n.notAvailable
    );
  };

  const ellips = (str, n) => {
    return str?.length > n ? str?.substr(0, n - 1) + '...' : str;
  };

  const extractUserInfo = (user) => ({
    id: user?.id,
    user: {
      givenName: user?.givenName,
      familyName: user?.familyName,
      //image: user?.thumbnail,
      name: user?.name,
      isVip: user?.isVip,
    },
  });

  const parseFromPubnubTimeToken = (date) => {
    const parsed = new Date(Math.ceil(date / 10000));

    return parsed;
  };

  // Create our number formatter.
  const currencyFormatter = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  return {
    capitalizeFirst,
    getHours,
    getDate,
    getLangCode,
    getFormattedDate,
    generateIcalBlob,
    generateAgendaBlob,
    getSummaryMessage,
    isBetween,
    getTooltipText,
    orderBreakoutRooms,
    checkPassedDate,
    checkEnterButtonIsDisabled,
    joinBr,
    joinChat,
    backToBr,
    parseRichText,
    getLocalizedParameter,
    ellips,
    extractUserInfo,
    parseFromPubnubTimeToken,
    currencyFormatter,
  };
};
